import { Compass, Target } from 'lucide-react';
import PageHero from '../components/sections/PageHero';
import CtaBanner from '../components/sections/CtaBanner';
import Container from '../components/ui/Container';
import Reveal from '../components/ui/Reveal';
import Icon from '../components/ui/Icon';
import StatCounter from '../components/ui/StatCounter';
import { impactStats } from '../data/stats';
import { site } from '../data/site';

export default function About() {
  return (
    <>
      <PageHero
        eyebrow="About us"
        title="A recycler built around the paperwork our clients are judged on"
        description={`Founded in ${site.founded}, ${site.legalName} processes e-waste and issues every statutory document.`}
      />

      {/* Story + stats */}
      <section className="section bg-white">
        <Container>
          <div className="grid gap-14 lg:grid-cols-[1.1fr_0.9fr] lg:gap-16">
            <Reveal direction="right">
              <span className="eyebrow">Our story</span>
              <h2 className="mt-5 text-3xl font-bold leading-tight tracking-tight sm:text-4xl">
                We started because good disposal was failing on documentation
              </h2>
              <div className="mt-6 space-y-4 text-base leading-relaxed text-ink-600">
                <p>
                  We operate a managed e-waste recovery and IT Asset Disposition (ITAD) platform, driven by data transparency and ESG-compliant market matching. Upon initiation, our on-ground audit team conducts a rigorous physical inspection of the assets, generating comprehensive, anonymized condition reports—complete with quantitative grading and visual documentation.
                </p>
                <p>
                 Rather than restricting clients to static, single-vendor processing rates, we leverage this verified dataset to solicit competitive bids exclusively from our closed network of EPR-authorized recycling facilities. This dynamic allocation ensures generators achieve optimal financial yield without compromising environmental integrity. Crucially, we govern the end-to-end chain of custody, ensuring that maximum value recovery is inextricably linked to strict regulatory adherence, seamless risk mitigation, and the delivery of audit-ready recycling certificates.
                </p>
                
              </div>
            </Reveal>

            <Reveal direction="left" delay={0.1}>
              <div className="grid gap-4 sm:grid-cols-2">
                {impactStats.slice(0, 4).map((stat) => (
                  <div
                    key={stat.id}
                    className="rounded-xl2 border border-ink-100 bg-surface p-6 transition-all duration-300 hover:-translate-y-1 hover:border-brand-200 hover:bg-white hover:shadow-soft"
                  >
                    <span className="grid h-10 w-10 place-items-center rounded-xl bg-white text-brand-700 shadow-soft">
                      <Icon name={stat.icon} className="h-5 w-5" />
                    </span>
                    <p className="mt-5 font-display text-2xl font-bold text-ink-900">
                      <StatCounter value={stat.value} suffix={stat.suffix} />
                    </p>
                    <p className="mt-1 text-xs font-medium text-ink-500">{stat.label}</p>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </Container>
      </section>

      {/* Mission & vision */}
      <section className="section bg-surface">
        <Container>
          <div className="grid gap-6 md:grid-cols-2">
            {[
              {
                icon: Target,
                title: 'Our mission',
                body: 'To make compliant e-waste disposal the most seamless option available to Indian enterprises—by managing the vendor bidding, the chain of custody, and the regulatory documentation so our clients only have to make one call.',
              },
              {
                icon: Compass,
                title: 'Our vision',
                body: 'A market where no recoverable material reaches a landfill and no disposal goes unrecorded, because doing it correctly costs less effort than doing it badly.',
              },
            ].map((block, index) => (
              <Reveal key={block.title} delay={index * 0.1}>
                <div className="h-full rounded-xl2 border border-ink-100 bg-white p-8 shadow-soft">
                  <span className="grid h-12 w-12 place-items-center rounded-xl bg-brand-50 text-brand-700">
                    <block.icon className="h-6 w-6" />
                  </span>
                  <h3 className="mt-6 font-display text-xl font-semibold text-ink-900">{block.title}</h3>
                  <p className="mt-3 leading-relaxed text-ink-600">{block.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      

      

      

      <CtaBanner />
    </>
  );
}
